import {useRef, useState} from 'react';
import {
  buildWebpImageUploadFormData,
  extractCdnUrlFromUploadResponse
} from '@/helpers/imageUploadForm';
import {getFileVerification} from '@/services/fileUploadService';
import {
  Constants,
  maxImageSizeInBytes
} from '@/utils/constants/commonConstants';
import {logger} from '@/helpers/logger';

const MAX_IMAGE_SIZE_IN_KB = Math.floor(maxImageSizeInBytes / 1024);

export const MAX_IMAGES = 5;
export const TABS = [
  {id: 'images', label: 'Rack Images'},
  {id: 'information', label: 'Rack Information'}
];

export const useRackModalBase = ({
  initialImages = [],
  initialInfo = {},
  actionInProgress
} = {}) => {
  const [activeTab, setActiveTab] = useState('images');
  const [images, setImages] = useState(initialImages);
  const [rackInformation, setRackInformation] = useState(initialInfo);
  const [uploadError, setUploadError] = useState('');
  const [publishError, setPublishError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadInProgress = useRef(false);
  const fileInput = useRef(null);
  const imageLimitReached = images.length >= MAX_IMAGES;

  const addImages = async (files) => {
    if (uploadInProgress.current || actionInProgress?.current) return;
    const selected = Array.from(files);
    if (selected.length === 0) return;

    if (images.length + selected.length > MAX_IMAGES) {
      setUploadError(`You can upload a maximum of ${MAX_IMAGES} images.`);
      return;
    }

    if (
      selected.some(
        (file) =>
          !/\.(jpe?g|png|webp)$/i.test(file.name) ||
          !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
      )
    ) {
      setUploadError('Please select only WEBP, JPG or PNG images.');
      return;
    }

    if (selected.some((file) => file.size === 0)) {
      setUploadError('Empty images cannot be uploaded.');
      return;
    }

    if (selected.some((file) => file.size > maxImageSizeInBytes)) {
      setUploadError(
        `Image size exceeds ${MAX_IMAGE_SIZE_IN_KB} KB. Please upload a smaller image.`
      );
      return;
    }

    uploadInProgress.current = true;
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');
    setPublishError('');
    let completed = 0;
    try {
      for (const file of selected) {
        const {formData} = await buildWebpImageUploadFormData(file);
        const response = await getFileVerification(
          formData,
          Constants.SalesTarget,
          (event) => {
            if (event.total > 0) {
              const fraction = Math.min(event.loaded / event.total, 1);
              setUploadProgress(
                Math.min(
                  99,
                  Math.round(((completed + fraction) / selected.length) * 100)
                )
              );
            }
          },
          'webp'
        );
        const imageUrl = extractCdnUrlFromUploadResponse(response);
        if (!imageUrl) {
          throw new Error('Image upload did not return a CDN URL.');
        }
        setImages((previous) => [
          ...previous,
          {
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
            imageUrl
          }
        ]);
        completed += 1;
        setUploadProgress(Math.round((completed / selected.length) * 100));
      }
    } catch (error) {
      logger.error('Rack image upload failed:', error);
      setUploadError(
        'Image upload failed. Previously uploaded images have been kept. Please retry the remaining images.'
      );
    } finally {
      uploadInProgress.current = false;
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleTabKeyDown = (event, index) => {
    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowLeft':
        nextIndex = (index + 1) % TABS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TABS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    setActiveTab(TABS[nextIndex].id);
    event.currentTarget.parentElement.children[nextIndex].focus();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    return addImages(event.dataTransfer.files);
  };

  const handleUploadClick = (disabled) => {
    if (!disabled) fileInput.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    event.target.value = '';
    return addImages(files);
  };

  const handleRemoveImage = (index) => {
    setImages((previous) =>
      previous.filter((_, itemIndex) => itemIndex !== index)
    );
    setUploadError('');
    setPublishError('');
  };

  const handleInformationChange = (name, value) => {
    setRackInformation((previous) => ({
      ...previous,
      [name]: value
    }));
    setPublishError('');
  };

  return {
    tabs: TABS,
    maxImages: MAX_IMAGES,
    activeTab,
    setActiveTab,
    images,
    rackInformation,
    uploadError,
    publishError,
    setPublishError,
    isDragging,
    isUploading,
    imageLimitReached,
    uploadProgress,
    fileInput,
    handleTabKeyDown,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleUploadClick,
    handleFileChange,
    handleRemoveImage,
    handleInformationChange
  };
};
